import type { SupabaseClient } from "@supabase/supabase-js";
import type { CommencementBundle } from "~/composables/useCommencement";
import type { UserRoleProfile } from "~/composables/useUserRole";
import { memberNeedsProjectBriefStart, resolveMemberOnboardingPhase } from "~/utils/memberOnboarding";
import {
  isMemberAllowedBeforeBrief,
  isMemberPortalPath,
} from "~/utils/memberPortalPaths";
import {
  middlewareApiGet,
  syncProfileToUserRoleState,
} from "~/utils/middlewareAuthProfile";
import { isStaffRole } from "~/utils/userRoles";

function prefetchCommencementBundle(
  supabase: SupabaseClient,
  bundle: { value: CommencementBundle | null },
) {
  void middlewareApiGet<CommencementBundle>(supabase, "/api/commencement/me")
    .then((data) => {
      bundle.value = data;
    })
    .catch(() => {});
}

export default defineNuxtRouteMiddleware(async (to) => {
  if (!isMemberPortalPath(to.path)) return;

  const nuxtApp = useNuxtApp();
  if (!nuxtApp.$supabase?.client) return;

  const supabase = useSupabaseClient();
  const user = useSupabaseUser();
  if (!user.value) return;

  const middlewareProfile = useState<UserRoleProfile | null | undefined>(
    "middleware:authProfile",
    () => undefined,
  );
  if (middlewareProfile.value !== undefined && middlewareProfile.value) {
    syncProfileToUserRoleState(middlewareProfile.value);
  }

  const { fetchProfile, isMember, isStaff, profile } = useUserRole();
  const cachedRole = profile.value?.role;

  if (cachedRole && isStaffRole(cachedRole)) return;

  if (!cachedRole) {
    await fetchProfile();
  }

  if (!isMember.value || isStaff.value) return;

  const bundle = useState<CommencementBundle | null>("commencementBundle", () => null);

  if (isMemberAllowedBeforeBrief(to.path)) {
    if (!bundle.value) {
      prefetchCommencementBundle(supabase, bundle);
    }
    return;
  }

  let data = bundle.value;

  if (!data) {
    try {
      data = await middlewareApiGet<CommencementBundle>(
        supabase,
        "/api/commencement/me",
      );
      bundle.value = data;
    } catch {
      return;
    }
  }

  if (memberNeedsProjectBriefStart(resolveMemberOnboardingPhase(data))) {
    return navigateTo("/dashboard");
  }
});
